import { useEffect, useRef, useState } from "react";
import { usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { toast } from "sonner";

const PAYPAL_MODE = (process.env.REACT_APP_PAYPAL_MODE || "sandbox").toLowerCase();

/* ---------------- Apple Pay (via PayPal SDK) ---------------- */
export function ApplePayButton({ amountUsd, createOrder, onCaptured, label }) {
  const [{ isResolved }] = usePayPalScriptReducer();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isResolved) return;
    const ok =
      typeof window !== "undefined" &&
      window.ApplePaySession &&
      window.ApplePaySession.supportsVersion(4) &&
      window.ApplePaySession.canMakePayments() &&
      window.paypal?.Applepay;
    if (!ok) return;
    window.paypal
      .Applepay()
      .config()
      .then((cfg) => {
        if (cfg?.isEligible) setConfig(cfg);
      })
      .catch((e) => console.warn("Apple Pay config failed", e));
  }, [isResolved]);

  if (!config) return null;

  const pay = () => {
    if (busy) return;
    const applepay = window.paypal.Applepay();
    const session = new window.ApplePaySession(4, {
      countryCode: config.countryCode,
      currencyCode: "USD",
      merchantCapabilities: config.merchantCapabilities,
      supportedNetworks: config.supportedNetworks,
      total: { label: "Aviary Studio", amount: amountUsd, type: "final" },
    });
    session.onvalidatemerchant = (event) => {
      applepay
        .validateMerchant({ validationUrl: event.validationURL, displayName: "Aviary Studio" })
        .then((payload) => session.completeMerchantValidation(payload.merchantSession))
        .catch((err) => {
          console.error("Apple Pay merchant validation failed", err);
          toast.error("Apple Pay validation failed. Is the domain registered in PayPal?");
          session.abort();
        });
    };
    session.onpaymentauthorized = async (event) => {
      setBusy(true);
      try {
        const orderId = await createOrder();
        await applepay.confirmOrder({
          orderId,
          token: event.payment.token,
          billingContact: event.payment.billingContact,
        });
        await onCaptured(orderId);
        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
      } catch (e) {
        console.error("Apple Pay payment failed", e);
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
        toast.error("Apple Pay payment failed");
      } finally {
        setBusy(false);
      }
    };
    session.oncancel = () => setBusy(false);
    session.begin();
  };

  return (
    <button
      type="button"
      onClick={pay}
      disabled={busy}
      className="w-full h-11 rounded bg-black text-white font-semibold text-sm flex items-center justify-center gap-1.5 hover:bg-black/85 transition-colors disabled:opacity-60"
      data-testid="apple-pay-button"
      aria-label={label || "Pay with Apple Pay"}
    >
      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white" aria-hidden="true">
        <path d="M17.05 12.54c-.03-2.63 2.15-3.89 2.25-3.95-1.22-1.79-3.13-2.04-3.81-2.07-1.62-.16-3.16.95-3.98.95-.82 0-2.09-.93-3.43-.9-1.77.03-3.4 1.03-4.31 2.61-1.84 3.19-.47 7.91 1.32 10.5.88 1.27 1.92 2.69 3.29 2.64 1.32-.05 1.82-.85 3.42-.85 1.6 0 2.05.85 3.44.82 1.42-.02 2.32-1.29 3.19-2.56 1-1.47 1.42-2.89 1.44-2.97-.03-.01-2.77-1.06-2.82-4.22zM14.44 4.83c.72-.88 1.21-2.1 1.08-3.32-1.04.04-2.31.7-3.06 1.57-.67.78-1.26 2.02-1.1 3.21 1.16.09 2.35-.59 3.08-1.46z" />
      </svg>
      Pay
    </button>
  );
}

/* ---------------- Google Pay (via PayPal SDK) ---------------- */
let gpayScriptPromise = null;
function loadGooglePayJs() {
  if (window.google?.payments?.api) return Promise.resolve();
  if (!gpayScriptPromise) {
    gpayScriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://pay.google.com/gp/p/js/pay.js";
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return gpayScriptPromise;
}

export function GooglePayButton({ amountUsd, createOrder, onCaptured }) {
  const [{ isResolved }] = usePayPalScriptReducer();
  const containerRef = useRef(null);
  const stateRef = useRef({ amountUsd, createOrder, onCaptured });
  const [ready, setReady] = useState(false);

  // Keep latest props for callbacks without re-initializing the client
  stateRef.current = { amountUsd, createOrder, onCaptured };

  useEffect(() => {
    if (!isResolved || !window.paypal?.Googlepay) return;
    let cancelled = false;

    const init = async () => {
      try {
        await loadGooglePayJs();
        const cfg = await window.paypal.Googlepay().config();
        if (cancelled || !cfg?.isEligible) return;

        const paymentsClient = new window.google.payments.api.PaymentsClient({
          environment: PAYPAL_MODE === "live" ? "PRODUCTION" : "TEST",
          paymentDataCallbacks: {
            onPaymentAuthorized: async (paymentData) => {
              const { createOrder: co, onCaptured: oc } = stateRef.current;
              try {
                const orderId = await co();
                const { status } = await window.paypal
                  .Googlepay()
                  .confirmOrder({ orderId, paymentMethodData: paymentData.paymentMethodData });
                if (status === "APPROVED") {
                  await oc(orderId);
                  return { transactionState: "SUCCESS" };
                }
                return {
                  transactionState: "ERROR",
                  error: { message: `Google Pay status: ${status}` },
                };
              } catch (e) {
                console.error("Google Pay payment failed", e);
                return {
                  transactionState: "ERROR",
                  error: { message: e?.message || "Payment failed" },
                };
              }
            },
          },
        });

        const isReady = await paymentsClient.isReadyToPay({
          apiVersion: cfg.apiVersion,
          apiVersionMinor: cfg.apiVersionMinor,
          allowedPaymentMethods: cfg.allowedPaymentMethods,
        });
        if (cancelled || !isReady.result || !containerRef.current) return;

        const button = paymentsClient.createButton({
          buttonType: "pay",
          buttonSizeMode: "fill",
          onClick: () => {
            const { amountUsd: amt } = stateRef.current;
            paymentsClient
              .loadPaymentData({
                apiVersion: cfg.apiVersion,
                apiVersionMinor: cfg.apiVersionMinor,
                allowedPaymentMethods: cfg.allowedPaymentMethods,
                merchantInfo: cfg.merchantInfo,
                transactionInfo: {
                  countryCode: cfg.countryCode || "US",
                  currencyCode: "USD",
                  totalPriceStatus: "FINAL",
                  totalPrice: String(amt),
                },
                callbackIntents: ["PAYMENT_AUTHORIZATION"],
              })
              .catch((e) => {
                if (e?.statusCode !== "CANCELED") {
                  console.error("Google Pay error", e);
                  toast.error("Google Pay failed to open");
                }
              });
          },
        });
        containerRef.current.innerHTML = "";
        containerRef.current.appendChild(button);
        setReady(true);
      } catch (e) {
        console.warn("Google Pay init failed", e);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [isResolved]);

  return (
    <div
      ref={containerRef}
      className={ready ? "w-full h-11 overflow-hidden rounded" : "hidden"}
      data-testid="google-pay-button"
    />
  );
}
